import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../components/ui/notify";
import {
  Modal,
  Upload,
  Button,
  Row,
  Col,
  Space,
  Card,
  ConfigProvider,
  Typography,
  Select,
  Input,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { HappyProvider } from "@ant-design/happy-work-theme";
import dayjs from "dayjs";
import { showInsetEffect } from "../../utils/antdInsetWave";
import { DataBadge } from "../../components/ui";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";

import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../assets/css/LaboralExpedientePruebasSection.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import EtapaProcesalJunta, { ETAPAS_JUNTA } from "../../components/tribunal/EtapaProcesalJunta";
import TabTerminacionJunta  from "../../components/tribunal/TabTerminacionJunta";
import TabCuantificaciones  from "../../components/tribunal/TabCuantificaciones";
import MovimientosCard      from "../../components/conciliacion/Movimientos";

import {
  actionJuntaGetById,
  actionJuntaDeleteById,
  actionJuntaUpdateById,
  actionJuntaUploadDocumento,
  actionJuntaDeleteDocumento,
  actionJuntaAuditLogGet,
  actionJuntaDefensaGet,
  actionJuntaDefensaUpsert,
  actionJuntaDefensaUploadDoc,
  actionJuntaDefensaDeleteDoc,
  actionJuntaEtapaGet,
} from "../../redux/actions/junta/junta";
import { actionAutoridadesJuntaGet } from "../../redux/actions/autoridades/autoridades";
import { actionEmpresasGet } from "../../redux/actions/empresas/empresas";
import { actionAbogadosGet } from "../../redux/actions/abogados/abogados";
import { PATH } from "../../redux/utils";

const { Title, Text } = Typography;

const safeArr = (v) => (Array.isArray(v) ? v : []);
const safeObj = (v) => (v && typeof v === "object" ? v : {});
const collapseSpaces = (s) => String(s || "").replace(/\s+/g, " ").trim();

const COMPETENCIA_OPTS = [
  { label: "Local", value: "Local" },
  { label: "Federal", value: "Federal" },
];

const TIPO_PROCEDIMIENTO_OPTS = [
  { label: "Ordinario", value: "Ordinario" },
  { label: "Especial (art. 892)", value: "Especial (art. 892)" },
  { label: "Paraprocesal o voluntario (art. 982)", value: "Paraprocesal o voluntario (art. 982)" },
  { label: "Colectivo", value: "Colectivo" },
];

const REGIMEN_REFORMA_2012_OPTS = [
  { label: "Antes de la reforma 2012", value: "antes" },
  { label: "Después de la reforma 2012", value: "despues" },
];

const REGIMEN_REFORMA_2012_LABELS = {
  antes: "Antes de la reforma 2012",
  despues: "Después de la reforma 2012",
};

const CARACTER_DEMANDADO_OPTS = [
  "Patrón solidario",
  "Socio o accionista",
  "Representante legal",
  "Administrador o apoderado",
  "Beneficiario del servicio",
  "Quien resulte responsable",
].map((c) => ({ label: c, value: c }));

const TIPO_ASESORIA_OPTS = ["Particular", "PROFEDET", "Sindicato", "Sin asesor"].map((o) => ({
  label: o,
  value: o,
}));

const ETAPA_PROCESAL_OPTS = [
  "Demanda presentada — pendiente de radicación",
  "Radicada — pendiente de emplazamiento",
  "Emplazada — audiencia trifásica señalada",
  "Audiencia de conciliación, demanda y excepciones celebrada",
  "Ofrecimiento y admisión de pruebas",
  "Desahogo de pruebas",
  "Alegatos",
  "Instrucción cerrada",
  "Proyecto de laudo / dictamen",
  "Laudo dictado",
  "Amparo directo",
  "Ejecución de laudo",
  "Convenio / desistimiento / caducidad",
  "Concluido y archivado",
].map((o) => ({ label: o, value: o }));

const ACCION_INTENTADA_OPTS = [
  "Despido injustificado — con reinstalación",
  "Despido injustificado — con indemnización constitucional",
  "Rescisión por causa imputable al patrón (art. 51)",
  "Cumplimiento de contrato / pago de prestaciones",
  "Derecho de preferencia y/o ascenso",
  "Titularidad de contrato colectivo (art. 389)",
  "Designación de beneficiarios (arts. 501-503)",
  "Riesgo de trabajo / incapacidad (arts. 487 y ss.)",
  "Conflicto colectivo de naturaleza económica",
  "Procedimiento paraprocesal o voluntario (art. 982)",
].map((o) => ({ label: o, value: o }));

const DILIGENCIA_OPTS = [
  "Audiencia de conciliación, demanda y excepciones (art. 873)",
  "Audiencia de ofrecimiento y admisión de pruebas (art. 880)",
  "Audiencia de desahogo de pruebas (art. 883)",
  "Comparecencia para absolución de posiciones (arts. 786 a 790)",
  "Desahogo de testimonial (art. 813)",
  "Diligencia de inspección (arts. 827 a 829)",
  "Ratificación de contenido y firma",
  "Audiencia incidental (art. 763)",
  "Alegatos (art. 885)",
  "Audiencia de discusión y votación del laudo (arts. 888 y 889)",
  "Diligencia de reinstalación",
  "Ratificación de convenio",
  "Diligencia de ejecución de laudo (art. 940)",
  "Otra diligencia",
].map((o) => ({ label: o, value: o }));

// Mismas opciones que usa PruebasSection.jsx en el formulario completo, y que
// usa la tarjeta "Pruebas del Expediente" de Tribunal, para que la edición
// inline aquí grabe exactamente los mismos valores.
const PERICIAL_TIPO_OPTIONS = [
  { label: "Caligráfica-Grafoscópica", value: "caligrafica_grafoscopica" },
  { label: "Fonográfica", value: "fonografica" },
  { label: "Contable", value: "contable" },
  { label: "Medios Electrónicos - Informática", value: "medios_electronicos_informatica" },
  { label: "Psicológica", value: "psicologica" },
  { label: "Médica", value: "medica" },
  { label: "Seguridad e Higiene", value: "seguridad_e_higiene" },
  { label: "Valuación", value: "valuacion" },
  { label: "Otros", value: "otros" },
];

const INSPECCION_OCULAR_TIPO_OPTIONS = [
  { label: "Recibos de pago", value: "recibos_pago" },
  { label: "Nóminas", value: "nominas" },
  { label: "Control de asistencia", value: "control_asistencia" },
  { label: "Tarjetas", value: "tarjetas" },
  { label: "Altas y bajas al seguro social", value: "altas_bajas_seguro_social" },
];

// El backend guarda cada registro pericial/inspección ocular con un "tipo" ya
// unido por " - " (varias opciones elegidas en un solo registro se juntan en
// una sola columna de texto); hay que separarlo de vuelta en array para
// mostrarlo/editarlo, igual que hace Tribunal.
const splitJoinedTipo = (s) => (s ? String(s).split(" - ").map((x) => x.trim()).filter(Boolean) : []);

const formatTipoList = (arr) => {
  const items = safeArr(arr).filter(Boolean);
  if (!items.length) return "—";
  return items.map((it) => toTitleCase(String(it).replaceAll("_", " "))).join(", ");
};

const coerceEmpresas = (slice) => {
  if (Array.isArray(slice)) return slice;
  if (Array.isArray(slice?.items)) return slice.items;
  if (Array.isArray(slice?.data)) return slice.data;
  if (Array.isArray(slice?.data?.items)) return slice.data.items;
  return [];
};

const coerceAutoridades = (slice) => {
  const d = slice?.data ?? slice;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

// Mismo patrón "label arriba / valor abajo" que usa la tarjeta "Datos del
// expediente" de Tribunal/Conciliación (laboral-exp-field-grid), para que
// las tres pantallas de detalle compartan estilo, iconos y tamaños de texto.
function Field({ label, value, span2 = false }) {
  return (
    <div className={`laboral-exp-field${span2 ? " laboral-exp-field--span2" : ""}`}>
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const fmtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("es-ES", { day: "2-digit" });
  const mon = d.toLocaleDateString("es-ES", { month: "short" }).replace(".", "");
  const yr = d.toLocaleDateString("es-ES", { year: "numeric" });
  return `${day} ${mon} ${yr}`.replace(" de ", " ");
};

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export default function LaboralJuntaConciliacionDetallePage() {
  const { idJunta } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");
  const [auditItems, setAuditItems] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // ── Edición inline de "Datos del Expediente" (mismo patrón que Tribunal) ──
  const [editingDatosCard, setEditingDatosCard] = useState(false);
  const [editingDatosForm, setEditingDatosForm] = useState({});
  const [savingDatosCard, setSavingDatosCard] = useState(false);
  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);

  // ── Edición inline de "Partes y Representación" ──
  const [editingPartesCard, setEditingPartesCard] = useState(false);
  const [editingPartesForm, setEditingPartesForm] = useState({});
  const [savingPartesCard, setSavingPartesCard] = useState(false);

  // ── Edición inline de "Estado del Expediente al Darlo de Alta" ──
  const [editingEstadoProcesalCard, setEditingEstadoProcesalCard] = useState(false);
  const [editingEstadoProcesalForm, setEditingEstadoProcesalForm] = useState({});
  const [savingEstadoProcesalCard, setSavingEstadoProcesalCard] = useState(false);

  // ── "Pruebas del Expediente" — copiado tal cual del detalle de Tribunal
  // (LaboralTribunalDetallePage.jsx), mismas tarjetas, mismo patrón de
  // edición inline por sección. ──
  const [editingConfesionalCard, setEditingConfesionalCard] = useState(false);
  const [editingConfesionalForm, setEditingConfesionalForm] = useState([]);
  const [savingConfesionalCard, setSavingConfesionalCard] = useState(false);

  const [editingConfesionalPropiosCard, setEditingConfesionalPropiosCard] = useState(false);
  const [editingConfesionalPropiosForm, setEditingConfesionalPropiosForm] = useState([]);
  const [savingConfesionalPropiosCard, setSavingConfesionalPropiosCard] = useState(false);

  const [editingConfesionalDemandadaCard, setEditingConfesionalDemandadaCard] = useState(false);
  const [editingConfesionalDemandadaForm, setEditingConfesionalDemandadaForm] = useState([]);
  const [savingConfesionalDemandadaCard, setSavingConfesionalDemandadaCard] = useState(false);

  // ---- Defensa (apartado libre en la vista principal: notas + documentos) ----
  const [defensa, setDefensa] = useState({ texto: "", documentos: [] });
  const [editingDefensaCard, setEditingDefensaCard] = useState(false);
  const [editingDefensaTexto, setEditingDefensaTexto] = useState("");
  const [savingDefensaCard, setSavingDefensaCard] = useState(false);
  const [uploadingDefensaDoc, setUploadingDefensaDoc] = useState(false);

  const [editingInformeAutoridadCard, setEditingInformeAutoridadCard] = useState(false);
  const [editingInformeAutoridadForm, setEditingInformeAutoridadForm] = useState([]);
  const [savingInformeAutoridadCard, setSavingInformeAutoridadCard] = useState(false);

  const [editingTestimonialCard, setEditingTestimonialCard] = useState(false);
  const [editingTestimonialForm, setEditingTestimonialForm] = useState([]);
  const [savingTestimonialCard, setSavingTestimonialCard] = useState(false);

  const [editingDocPublicaCard, setEditingDocPublicaCard] = useState(false);
  const [editingDocPublicaForm, setEditingDocPublicaForm] = useState([]);
  const [savingDocPublicaCard, setSavingDocPublicaCard] = useState(false);

  const [editingDocPrivadaCard, setEditingDocPrivadaCard] = useState(false);
  const [editingDocPrivadaForm, setEditingDocPrivadaForm] = useState([]);
  const [savingDocPrivadaCard, setSavingDocPrivadaCard] = useState(false);

  const [editingPericialCard, setEditingPericialCard] = useState(false);
  const [editingPericialForm, setEditingPericialForm] = useState([]);
  const [savingPericialCard, setSavingPericialCard] = useState(false);

  const [editingInspeccionCard, setEditingInspeccionCard] = useState(false);
  const [editingInspeccionForm, setEditingInspeccionForm] = useState([]);
  const [savingInspeccionCard, setSavingInspeccionCard] = useState(false);

  const [savingChecklistKey, setSavingChecklistKey] = useState(null);

  const autoridadesSlice = useSelector((s) => s.autoridades || {});
  const empresasSlice = useSelector((s) => s.empresas || {});
  const abogadosSlice = useSelector((s) => s.abogados || {});

  const { estadosOptions, ciudadesOptions, ciudadesById } = useLaboralCatalogos(
    selectedEstadoId,
    selectedCiudadId,
    { modulo: "junta" }
  );

  const autoridadesRaw = useMemo(() => coerceAutoridades(autoridadesSlice), [autoridadesSlice]);

  const autoridadOptions = useMemo(() => {
    if (!selectedCiudadId) return [];
    return autoridadesRaw
      .filter((a) => String(a.id_ciudad || "") === String(selectedCiudadId))
      .map((a) => ({ label: collapseSpaces(a.nombre || ""), value: a.id_autoridad ?? a.id }))
      .filter((o) => o.label);
  }, [autoridadesRaw, selectedCiudadId]);

  const ciudadesIdsConAutoridad = useMemo(() => {
    const s = new Set();
    autoridadesRaw.forEach((a) => { if (a.id_ciudad != null) s.add(Number(a.id_ciudad)); });
    return s;
  }, [autoridadesRaw]);

  const estadosOptionsFinal = estadosOptions;

  const ciudadesOptionsFinal = useMemo(() => {
    if (!selectedEstadoId) return [];
    return (ciudadesOptions || []).filter((o) => ciudadesIdsConAutoridad.has(Number(o.value)));
  }, [ciudadesOptions, ciudadesIdsConAutoridad, selectedEstadoId]);

  const empresasItems = useMemo(() => coerceEmpresas(empresasSlice), [empresasSlice]);

  const abogadosItems = useMemo(() => coerceEmpresas(abogadosSlice), [abogadosSlice]);
  const abogadosOptions = useMemo(
    () =>
      abogadosItems
        .map((a) => ({ label: collapseSpaces(a?.nombre || ""), value: a?.id_abogado ?? a?.id }))
        .filter((o) => o.label)
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [abogadosItems]
  );
  const abogadoResponsableName = useMemo(() => {
    if (!detalle?.id_abogado_responsable) return "—";
    const ab = abogadosItems.find(
      (a) => String(a?.id_abogado ?? a?.id) === String(detalle.id_abogado_responsable)
    );
    return ab?.nombre || "—";
  }, [abogadosItems, detalle?.id_abogado_responsable]);

  const actoresList = useMemo(
    () => collapseSpaces(detalle?.nombre_actora || "").split(" - ").map((s) => s.trim()).filter(Boolean),
    [detalle?.nombre_actora]
  );

  const demandadosList = useMemo(
    () =>
      safeArr(detalle?.razones_sociales).map((rs) => ({
        id_razon_social: rs?.id_razon_social ?? rs?.id ?? null,
        nombre: rs?.razon_social || "—",
        caracter: rs?.caracter || "",
      })),
    [detalle?.razones_sociales]
  );

  const todasRazonesOptions = useMemo(() => {
    const seen = new Set();
    const all = [];
    empresasItems.forEach((emp) => {
      const rsArr = Array.isArray(emp.razones_sociales) ? emp.razones_sociales : [];
      rsArr.forEach((rs) => {
        const label = collapseSpaces(rs?.nombre || rs?.razon_social || "");
        const value = String(rs?.id ?? rs?.id_empresa_razon_social ?? "");
        if (label && value && !seen.has(value)) {
          seen.add(value);
          all.push({ label, value });
        }
      });
    });
    // Además de las razones sociales del catálogo global de empresas,
    // agregamos las que ya están ligadas a este expediente (detalle.razones_sociales)
    // aunque no aparezcan en `empresasItems` (ej. empresa inactiva, catálogo aún
    // sin cargar). Sin esto, el Select no encuentra la opción y antd muestra el
    // id crudo en vez del nombre.
    safeArr(detalle?.razones_sociales).forEach((rs) => {
      const value = String(rs?.id_razon_social ?? rs?.id ?? "");
      const label = collapseSpaces(rs?.nombre || rs?.razon_social || "");
      if (label && value && !seen.has(value)) {
        seen.add(value);
        all.push({ label, value });
      }
    });
    return all.sort((a, b) => a.label.localeCompare(b.label));
  }, [empresasItems, detalle?.razones_sociales]);

  useEffect(() => {
    dispatch(actionAutoridadesJuntaGet());
    dispatch(actionEmpresasGet());
    dispatch(actionAbogadosGet());
  }, [dispatch]);

  const startEditDatosCard = () => {
    if (detalle?.id_autoridad) {
      const au = autoridadesRaw.find(
        (a) => String(a.id_autoridad ?? a.id) === String(detalle.id_autoridad)
      );
      const idCiudad = au?.id_ciudad ?? detalle?.id_ciudad ?? null;
      setSelectedCiudadId(idCiudad ?? null);
      const cObj = ciudadesById?.[String(idCiudad)];
      const idEstado = cObj?.id_estado ?? detalle?.id_estado ?? null;
      setSelectedEstadoId(idEstado ?? null);
    } else {
      setSelectedEstadoId(null);
      setSelectedCiudadId(null);
    }
    setSelectedAutoridadId(detalle?.id_autoridad ? String(detalle.id_autoridad) : null);

    setEditingDatosForm({
      expediente: detalle?.expediente || "",
      nombre_actora: detalle?.nombre_actora || "",
      abogado: detalle?.abogado || "",
      corresponsal_nombre: detalle?.corresponsal_nombre || "",
      competencia: detalle?.competencia || "",
      tipo_procedimiento: detalle?.tipo_procedimiento || "",
      regimen_reforma_2012: detalle?.regimen_reforma_2012 || "",
      expedientes_relacionados: detalle?.expedientes_relacionados || "",
      razones_sociales_ids: safeArr(detalle?.razones_sociales)
        .map((rs) => String(rs.id_razon_social ?? rs.id ?? ""))
        .filter(Boolean),
    });
    setEditingDatosCard(true);
  };

  const handleSaveDatosCard = async () => {
    const expediente = (editingDatosForm.expediente || "").trim();
    const nombreActora = (editingDatosForm.nombre_actora || "").trim();

    if (!expediente) {
      notify.error({ message: "Falta el número de expediente", placement: "bottomRight" });
      return;
    }
    if (!nombreActora) {
      notify.error({ message: "Falta el nombre de la parte actora", placement: "bottomRight" });
      return;
    }
    if (!selectedAutoridadId) {
      notify.error({ message: "Selecciona una autoridad", placement: "bottomRight" });
      return;
    }

    setSavingDatosCard(true);
    try {
      const payload = {
        expediente,
        nombre_actora: nombreActora,
        id_autoridad: Number(selectedAutoridadId),
        razones_sociales_ids: editingDatosForm.razones_sociales_ids || [],
        corresponsal_nombre: (editingDatosForm.corresponsal_nombre || "").trim() || null,
        abogado: (editingDatosForm.abogado || "").trim() || null,
        competencia: editingDatosForm.competencia || null,
        tipo_procedimiento: editingDatosForm.tipo_procedimiento || null,
        regimen_reforma_2012: editingDatosForm.regimen_reforma_2012 || null,
        expedientes_relacionados: (editingDatosForm.expedientes_relacionados || "").trim() || null,
        // El backend (update_junta) reemplaza TODO el bloque pruebas_documentacion
        // con lo que reciba en el PUT; si esta tarjeta no lo reenvía, se borra
        // todo lo guardado en "Pruebas del Expediente" (confesional, testigos,
        // pericial, etc.). Por eso siempre se reconstruye y reenvía el bloque
        // actual completo, igual que hace Tribunal en cada guardado.
        pruebas_documentacion: buildPruebasDocumentacionPayload(),
      };

      await dispatch(actionJuntaUpdateById(idJunta, payload, {}));
      await load();
      setEditingDatosCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingDatosCard(false);
    }
  };

  // Guarda el panel "Administrar partes" del tab Proceso (Audiencia CDE):
  // reutiliza los mismos campos que "Datos del Expediente" (nombre_actora y
  // razones_sociales_ids) — no hay una tabla de "partes" independiente.
  const handleSavePartesJuicio = async (actoresArr, razonesIds) => {
    try {
      const nombreActora = (actoresArr || []).map((a) => (a || "").trim()).filter(Boolean).join(" - ");
      await dispatch(
        actionJuntaUpdateById(idJunta, {
          expediente: detalle?.expediente || "",
          nombre_actora: nombreActora,
          id_autoridad: detalle?.id_autoridad,
          razones_sociales_ids: razonesIds || [],
          pruebas_documentacion: buildPruebasDocumentacionPayload(),
        }, {})
      );
      await load();
      notify.success({ message: "Partes actualizadas", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    }
  };

  // ---- Partes y Representación ----
  const startEditPartesCard = () => {
    const actoresIniciales = actoresList.length ? actoresList : [""];
    const demandadosIniciales = safeArr(detalle?.razones_sociales)
      .filter((rs) => (rs?.id_razon_social ?? rs?.id) != null)
      .map((rs) => ({
        _id: genRowId(),
        id_razon_social: rs.id_razon_social ?? rs.id,
        caracter: rs.caracter || "",
      }));

    setEditingPartesForm({
      actores: actoresIniciales,
      demandados: demandadosIniciales.length
        ? demandadosIniciales
        : [{ _id: genRowId(), id_razon_social: null, caracter: "" }],
      tipo_asesoria_actora: detalle?.tipo_asesoria_actora || "",
      id_abogado_responsable: detalle?.id_abogado_responsable || null,
    });
    setEditingPartesCard(true);
  };

  const handleSavePartesCard = async () => {
    const actoresLimpios = safeArr(editingPartesForm.actores).map((a) => collapseSpaces(a)).filter(Boolean);
    const demandadosLimpios = safeArr(editingPartesForm.demandados)
      .filter((d) => d.id_razon_social)
      .map((d) => ({ id_razon_social: d.id_razon_social, caracter: d.caracter || null }));

    if (!actoresLimpios.length) {
      notify.error({ message: "Agrega al menos una parte actora", placement: "bottomRight" });
      return;
    }
    if (!demandadosLimpios.length) {
      notify.error({ message: "Agrega al menos un demandado", placement: "bottomRight" });
      return;
    }

    setSavingPartesCard(true);
    try {
      const payload = {
        nombre_actora: actoresLimpios.join(" - "),
        razones_sociales_ids: demandadosLimpios,
        tipo_asesoria_actora: editingPartesForm.tipo_asesoria_actora || null,
        id_abogado_responsable: editingPartesForm.id_abogado_responsable
          ? Number(editingPartesForm.id_abogado_responsable)
          : null,
      };
      await dispatch(actionJuntaUpdateById(idJunta, payload, {}));
      await load();
      setEditingPartesCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingPartesCard(false);
    }
  };

  // ---- Estado del Expediente al Darlo de Alta ----
  const startEditEstadoProcesalCard = () => {
    setEditingEstadoProcesalForm({
      etapa_procesal_inicial: detalle?.etapa_procesal_inicial || "",
      accion_intentada: detalle?.accion_intentada || "",
      fecha_presentacion_demanda: detalle?.fecha_presentacion_demanda ? dayjs(detalle.fecha_presentacion_demanda) : null,
      fecha_recepcion: detalle?.fecha_recepcion ? dayjs(detalle.fecha_recepcion) : null,
      fecha_emplazamiento: detalle?.fecha_emplazamiento ? dayjs(detalle.fecha_emplazamiento) : null,
      fecha_ultima_actuacion: detalle?.fecha_ultima_actuacion ? dayjs(detalle.fecha_ultima_actuacion) : null,
      fecha_proxima_diligencia: detalle?.fecha_proxima_diligencia ? dayjs(detalle.fecha_proxima_diligencia) : null,
      descripcion_proxima_diligencia: detalle?.descripcion_proxima_diligencia || "",
    });
    setEditingEstadoProcesalCard(true);
  };

  const handleSaveEstadoProcesalCard = async () => {
    setSavingEstadoProcesalCard(true);
    try {
      const f = editingEstadoProcesalForm;
      const payload = {
        etapa_procesal_inicial: f.etapa_procesal_inicial || null,
        accion_intentada: f.accion_intentada || null,
        fecha_presentacion_demanda: f.fecha_presentacion_demanda ? f.fecha_presentacion_demanda.format("YYYY-MM-DD") : null,
        fecha_recepcion: f.fecha_recepcion ? f.fecha_recepcion.format("YYYY-MM-DD") : null,
        fecha_emplazamiento: f.fecha_emplazamiento ? f.fecha_emplazamiento.format("YYYY-MM-DD") : null,
        fecha_ultima_actuacion: f.fecha_ultima_actuacion ? f.fecha_ultima_actuacion.format("YYYY-MM-DD") : null,
        fecha_proxima_diligencia: f.fecha_proxima_diligencia ? f.fecha_proxima_diligencia.format("YYYY-MM-DD") : null,
        descripcion_proxima_diligencia: f.descripcion_proxima_diligencia || null,
      };
      await dispatch(actionJuntaUpdateById(idJunta, payload, {}));
      await load();
      setEditingEstadoProcesalCard(false);
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingEstadoProcesalCard(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const data = await dispatch(actionJuntaGetById(idJunta));
      const d = data?.detalle || data?.data || data;
      setDetalle(d && typeof d === "object" ? d : null);
    } catch (e) {
      notify.error({
        message: "Error al cargar",
        description: e?.response?.data?.detail || e?.message,
      });
      setDetalle(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [idJunta]);


  useEffect(() => {
    if (!idJunta) return;
    dispatch(actionJuntaAuditLogGet(idJunta))
      .then((data) => setAuditItems(data?.items || []))
      .catch(() => setAuditItems([]));
  }, [dispatch, idJunta]);

  // Próxima audiencia agendada (la más reciente entre todas las etapas del
  // proceso) — se muestra en el cuadro azul de la pestaña "Principal",
  // igual que en el expediente de Centro de Conciliación. Se vuelve a
  // cargar cada vez que se guarda/elimina una fecha en la pestaña
  // "Proceso" (ver prop onFechaAudienciaChanged de EtapaProcesalJunta),
  // para que ambas pestañas queden siempre sincronizadas.
  const [proximaAudienciaJunta, setProximaAudienciaJunta] = useState(null);

  const loadProximaAudienciaJunta = useCallback(async () => {
    if (!idJunta) return;
    try {
      const results = await Promise.all(
        ETAPAS_JUNTA.map((etapa) =>
          dispatch(actionJuntaEtapaGet(idJunta, etapa.n)).catch(() => null),
        ),
      );
      let latest = null;
      results.forEach((res) => {
        (res?.items || []).forEach((item) => {
          if (!item?.fecha_audiencia) return;
          const d = dayjs(item.fecha_audiencia);
          if (d.isValid() && (!latest || d.isAfter(latest))) {
            latest = d;
          }
        });
      });
      setProximaAudienciaJunta(latest);
    } catch {
      setProximaAudienciaJunta(null);
    }
  }, [dispatch, idJunta]);

  useEffect(() => {
    loadProximaAudienciaJunta();
  }, [loadProximaAudienciaJunta]);

  const loadDefensa = useCallback(async () => {
    if (!idJunta) return;
    try {
      const data = await dispatch(actionJuntaDefensaGet(idJunta));
      setDefensa({ texto: data?.texto || "", documentos: safeArr(data?.documentos) });
    } catch {
      setDefensa({ texto: "", documentos: [] });
    }
  }, [dispatch, idJunta]);

  useEffect(() => {
    loadDefensa();
  }, [loadDefensa]);

  const startEditDefensaCard = () => {
    setEditingDefensaTexto(defensa.texto || "");
    setEditingDefensaCard(true);
  };

  const handleSaveDefensaCard = async () => {
    setSavingDefensaCard(true);
    try {
      await dispatch(actionJuntaDefensaUpsert(idJunta, editingDefensaTexto));
      await loadDefensa();
      setEditingDefensaCard(false);
      notify.success({ message: "Defensa guardada", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar la defensa",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSavingDefensaCard(false);
    }
  };

  const handleUploadDefensaDoc = async (file) => {
    setUploadingDefensaDoc(true);
    try {
      await dispatch(actionJuntaDefensaUploadDoc(idJunta, file));
      await loadDefensa();
      notify.success({ message: "Documento cargado", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "Error al subir documento",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setUploadingDefensaDoc(false);
    }
    return false;
  };

  const handleDeleteDefensaDoc = async (doc) => {
    try {
      await dispatch(actionJuntaDefensaDeleteDoc(idJunta, doc.id_junta_defensa_documento || doc.id));
      await loadDefensa();
      notify.success({ message: "Documento eliminado", placement: "bottomRight" });
    } catch (e) {
      notify.error({
        message: "Error al eliminar documento",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar expediente",
      content:
        "El expediente se desactivará y no aparecerá en el listado. Esta acción no se puede deshacer.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      okButtonProps: { loading: deleting, disabled: deleting },
      cancelButtonProps: { disabled: deleting },
      onOk: async () => {
        try {
          setDeleting(true);
          await dispatch(actionJuntaDeleteById(idJunta));
          notify.success({ message: "Expediente eliminado" });
          navigate("/materias/laboral/junta-conciliacion");
        } catch (e) {
          notify.error({
            message: "Error",
            description: e?.response?.data?.detail || e?.message,
          });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleUploadDoc = async ({ file }) => {
    try {
      setUploadingDoc(true);
      await dispatch(actionJuntaUploadDocumento(idJunta, file));
      notify.success({ message: "Documento subido" });
      await load();
    } catch (e) {
      notify.error({
        message: "Error al subir",
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setUploadingDoc(false);
    }
  };

  const openJuntaDoc = (doc) => {
    if (!doc?.url) return;
    window.open(`${PATH}${doc.url}`, "_blank", "noopener,noreferrer");
  };

  const handleDeleteDoc = (idDoc) => {
    Modal.confirm({
      title: "Eliminar documento",
      content: "¿Confirmas eliminar este documento?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        try {
          setDeletingDocId(idDoc);
          await dispatch(actionJuntaDeleteDocumento(idJunta, idDoc));
          notify.success({ message: "Documento eliminado" });
          await load();
        } catch (e) {
          notify.error({
            message: "Error al eliminar",
            description: e?.response?.data?.detail || e?.message,
          });
        } finally {
          setDeletingDocId(null);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <div className="lxp-card lxp-summary">Cargando expediente...</div>
        </div>
      </div>
    );
  }

  if (!detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <div className="lxp-card lxp-summary">No se encontró el expediente.</div>
        </div>
      </div>
    );
  }

  const isActive = Number(detalle?.active ?? 1) === 1;
  const actorName = detalle?.nombre_actora || "—";
  const razonesSociales = safeArr(detalle?.razones_sociales);
  const rsNames = razonesSociales.map(rs => rs.nombre || rs.razon_social || "").filter(Boolean);
  const empresaName = rsNames.length > 0 ? rsNames.join(" / ") : (detalle?.nombre_empresa || "—");
  const autoridadName = detalle?.nombre_autoridad || "—";
  const ciudadName = detalle?.nombre_ciudad || "";
  const estadoName = detalle?.nombre_estado || "";
  const ubicacionName = [ciudadName, estadoName].filter(Boolean).join(", ") || "—";
  const documentos = safeArr(detalle?.documentos);
  const estatusPill = isActive
    ? { label: "ACTIVO", kind: "primary" }
    : { label: "ARCHIVADO", kind: "gray" };

  // ============================================================
  // "Pruebas del Expediente" — mismas variables derivadas que usa
  // LaboralTribunalDetallePage.jsx (líneas ~497-552), adaptadas para leer
  // de `detalle` (estado local) en vez de una variable conectada a Redux.
  // El GET /junta-conciliacion/{id} regresa `pruebas_documentacion` con el
  // mismo shape exacto que Tribunal.
  // ============================================================
  const pruebas = safeObj(detalle?.pruebas_documentacion);
  const pruebasDetalle = safeObj(pruebas?.detalle);
  const pruebasActora = safeObj(pruebasDetalle?.actora);
  const pruebasDemandada = safeObj(pruebasDetalle?.demandada);

  const confesionalesActora = safeArr(pruebasActora?.confesional);
  const confesionalesPropiosActora = safeArr(pruebasActora?.confesional_hechos_propios);
  const confesionalesDemandada = safeArr(pruebasDemandada?.confesional);

  const testimonialActora = safeObj(pruebasActora?.testimonial);
  const testigosActora = safeArr(testimonialActora?.testigos);

  const documentalPublicaActora = safeObj(pruebasActora?.documental_publica);
  const documentosPublicosActora = safeArr(documentalPublicaActora?.documentos);

  const documentalPrivadaActora = safeObj(pruebasActora?.documental_privada);
  const documentosPrivadosActora = safeArr(documentalPrivadaActora?.documentos);

  const pericialActora = safeObj(pruebasActora?.pericial);
  const pericialItemsActora = safeArr(pericialActora?.items);

  const inspeccionOcularActora = safeObj(pruebasActora?.inspeccion_ocular);
  const inspeccionItemsActora = safeArr(inspeccionOcularActora?.items);

  const informeAutoridadActora = safeObj(pruebasActora?.informe_autoridad);
  const informesAutoridadActora = safeArr(informeAutoridadActora?.documentos);

  const mediosElectronicosActora = safeObj(pruebasActora?.medios_electronicos);

  const checklistActora = safeObj(pruebas?.checklist?.actora);
  const checklistDemandadaGlobal = safeObj(pruebas?.checklist?.demandada);
  const tieneConfesionalExpresa = Boolean(checklistActora?.confesional_expresa);
  const tieneInstrumentalActora = Boolean(checklistActora?.instrumental_actuaciones);
  const tienePresuncionalActora = Boolean(checklistActora?.presuncional_legal_humana);

  const razonesSocialesDemandadaOptions = razonesSociales
    .filter((rs) => (rs?.id_razon_social ?? rs?.id) != null)
    .map((rs) => {
      const id = rs.id_razon_social ?? rs.id;
      // Preferimos el nombre que ya viene en `detalle.razones_sociales`; si por
      // algún motivo llega vacío (join sin match), buscamos en el catálogo
      // global de empresas antes de recurrir a un texto genérico — así nunca
      // se le muestra al usuario el id crudo como si fuera el nombre.
      const fallback = todasRazonesOptions.find((o) => o.value === String(id))?.label;
      return {
        label: rs?.nombre || rs?.razon_social || fallback || "Razón social sin nombre",
        value: id,
      };
    });

  const genRowId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  // ============================================================
  // Igual que buildPruebasDocumentacionPayload en Tribunal: reconstruye el
  // bloque completo de "pruebas_documentacion" con los nombres de campo que
  // espera el PUT (distintos a los del GET en pericial/inspección ocular),
  // y solo pisa la sección que la tarjeta en edición realmente cambió.
  // ============================================================
  const buildPruebasDocumentacionPayload = (
    overridesActora = {},
    overridesChecklist = {},
    overridesDemandada = {}
  ) => {
    const confesionalDefault = confesionalesActora.map((item) => ({
      persona_confesar: item?.persona_confesar || item?.razon_social || "",
      hechos_confesar: item?.hechos_confesar || "",
    }));

    const confesionalPropiosDefault = confesionalesPropiosActora.map((item) => ({
      tipo_prueba: item?.tipo_prueba || "",
      hechos_propios: item?.hechos_propios || "",
    }));

    const confesionalDemandadaDefault = confesionalesDemandada.map((item) => ({
      id_razon_social: item?.id_razon_social ?? null,
    }));

    const pericialDefault = pericialItemsActora.map((item) => ({
      tipo_pericial: splitJoinedTipo(item?.tipo),
      nombre_perito: item?.perito || "",
      objeto_prueba: item?.objeto || "",
    }));

    const inspeccionDefault = inspeccionItemsActora.map((item) => ({
      tipo_inspeccion_ocular: splitJoinedTipo(item?.tipo),
      nombre_inspeccion_ocular: item?.nombre || "",
      objeto_prueba: item?.objeto || "",
    }));

    const testimonialDefault = {
      tipo: safeArr(testimonialActora?.tipo),
      testigos: testigosActora.map((t) => ({
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      })),
    };

    const documentalPublicaDefault = {
      documentos: documentosPublicosActora.map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    const documentalPrivadaDefault = {
      documentos: documentosPrivadosActora.map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    const informeAutoridadDefault = {
      documentos: informesAutoridadActora.map((d) => ({
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha || "",
      })),
    };

    return {
      tab: "actora",
      checklist: {
        actora: {
          ...(pruebas?.checklist?.actora || {}),
          ...overridesChecklist,
        },
      },
      detalle: {
        actora: {
          confesional: confesionalDefault,
          confesional_hechos_propios: confesionalPropiosDefault,
          testimonial: testimonialDefault,
          documental_publica: documentalPublicaDefault,
          documental_privada: documentalPrivadaDefault,
          informe_autoridad: informeAutoridadDefault,
          pericial: pericialDefault,
          inspeccion_ocular: inspeccionDefault,
          medios_electronicos: {
            tipos: safeArr(mediosElectronicosActora?.tipos),
            descripcion: mediosElectronicosActora?.descripcion || "",
          },
          ...overridesActora,
        },
        demandada: {
          confesional: confesionalDemandadaDefault,
          ...overridesDemandada,
        },
      },
      evidencias_actora: [],
    };
  };

  // ============================================================
  // Guarda cualquier tarjeta de "Pruebas del Expediente": siempre reenvía el
  // bloque `pruebas_documentacion` completo (con la sección editada pisada
  // por overrides) para no perder lo guardado en las demás tarjetas — el
  // backend (update_junta) reemplaza TODO el bloque pruebas con lo que
  // reciba, así que omitirlo o mandarlo vacío borra confesional/testigos/
  // pericial/etc. ya guardados.
  // ============================================================
  const savePruebaSection = async (
    { pruebasActora: overridesActora, pruebasDemandada: overridesDemandada, pruebasChecklist: overridesChecklist } = {},
    { onDone, onFail, setSaving }
  ) => {
    setSaving(true);
    try {
      const payload = {
        pruebas_documentacion: buildPruebasDocumentacionPayload(
          overridesActora,
          overridesChecklist,
          overridesDemandada
        ),
      };
      await dispatch(actionJuntaUpdateById(idJunta, payload, {}));
      await load();
      onDone?.();
      notify.success({
        message: "Expediente actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
      onFail?.();
    } finally {
      setSaving(false);
    }
  };

  // ---- Confesional ----
  const startEditConfesionalCard = () => {
    setEditingConfesionalForm(
      confesionalesActora.map((item) => ({
        _id: genRowId(),
        persona: item?.persona_confesar || "",
        hechos: item?.hechos_confesar || "",
      }))
    );
    setEditingConfesionalCard(true);
  };

  const handleSaveConfesionalCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          confesional: editingConfesionalForm.map((item) => ({
            persona_confesar: (item.persona || "").trim(),
            hechos_confesar: (item.hechos || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingConfesionalCard(false), setSaving: setSavingConfesionalCard }
    );

  // ---- Confesional de Hechos Propios ----
  const startEditConfesionalPropiosCard = () => {
    setEditingConfesionalPropiosForm(
      confesionalesPropiosActora.map((item) => ({
        _id: genRowId(),
        tipo_prueba: item?.tipo_prueba || "",
        hechos_propios: item?.hechos_propios || "",
      }))
    );
    setEditingConfesionalPropiosCard(true);
  };

  const handleSaveConfesionalPropiosCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          confesional_hechos_propios: editingConfesionalPropiosForm.map((item) => ({
            tipo_prueba: (item.tipo_prueba || "").trim(),
            hechos_propios: (item.hechos_propios || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingConfesionalPropiosCard(false), setSaving: setSavingConfesionalPropiosCard }
    );

  // ---- Confesional Demandada ----
  const startEditConfesionalDemandadaCard = () => {
    setEditingConfesionalDemandadaForm(
      confesionalesDemandada.map((item) => ({
        _id: genRowId(),
        id_razon_social: item?.id_razon_social ?? null,
      }))
    );
    setEditingConfesionalDemandadaCard(true);
  };

  const handleSaveConfesionalDemandadaCard = () =>
    savePruebaSection(
      {
        pruebasDemandada: {
          confesional: editingConfesionalDemandadaForm
            .filter((item) => item.id_razon_social != null)
            .map((item) => ({ id_razon_social: item.id_razon_social })),
        },
      },
      {
        onDone: () => setEditingConfesionalDemandadaCard(false),
        setSaving: setSavingConfesionalDemandadaCard,
      }
    );

  // ---- Informe de Autoridad ----
  const startEditInformeAutoridadCard = () => {
    setEditingInformeAutoridadForm(
      informesAutoridadActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingInformeAutoridadCard(true);
  };

  const handleSaveInformeAutoridadCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          informe_autoridad: {
            documentos: editingInformeAutoridadForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingInformeAutoridadCard(false), setSaving: setSavingInformeAutoridadCard }
    );

  // ---- Testimonial ----
  const startEditTestimonialCard = () => {
    setEditingTestimonialForm(
      testigosActora.map((t) => ({
        _id: genRowId(),
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      }))
    );
    setEditingTestimonialCard(true);
  };

  const handleSaveTestimonialCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          testimonial: {
            tipo: safeArr(testimonialActora?.tipo),
            testigos: editingTestimonialForm.map((t) => ({
              nombre: (t.nombre || "").trim(),
              relacion: (t.relacion || "").trim(),
              contacto: (t.contacto || "").trim(),
            })),
          },
        },
      },
      { onDone: () => setEditingTestimonialCard(false), setSaving: setSavingTestimonialCard }
    );

  // ---- Documental Pública ----
  const startEditDocPublicaCard = () => {
    setEditingDocPublicaForm(
      documentosPublicosActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingDocPublicaCard(true);
  };

  const handleSaveDocPublicaCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          documental_publica: {
            documentos: editingDocPublicaForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingDocPublicaCard(false), setSaving: setSavingDocPublicaCard }
    );

  // ---- Documental Privada ----
  const startEditDocPrivadaCard = () => {
    setEditingDocPrivadaForm(
      documentosPrivadosActora.map((d) => ({
        _id: genRowId(),
        nombre: d?.nombre || "",
        descripcion: d?.descripcion || "",
        fecha: d?.fecha ? dayjs(d.fecha) : null,
      }))
    );
    setEditingDocPrivadaCard(true);
  };

  const handleSaveDocPrivadaCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          documental_privada: {
            documentos: editingDocPrivadaForm.map((d) => ({
              nombre: (d.nombre || "").trim(),
              descripcion: (d.descripcion || "").trim(),
              fecha: d.fecha ? d.fecha.format("YYYY-MM-DD") : null,
            })),
          },
        },
      },
      { onDone: () => setEditingDocPrivadaCard(false), setSaving: setSavingDocPrivadaCard }
    );

  // ---- Pericial ----
  const startEditPericialCard = () => {
    setEditingPericialForm(
      pericialItemsActora.map((item) => ({
        _id: genRowId(),
        tipo: splitJoinedTipo(item?.tipo),
        perito: item?.perito || "",
        objeto: item?.objeto || "",
      }))
    );
    setEditingPericialCard(true);
  };

  const handleSavePericialCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          pericial: editingPericialForm.map((item) => ({
            tipo_pericial: item.tipo || [],
            nombre_perito: (item.perito || "").trim(),
            objeto_prueba: (item.objeto || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingPericialCard(false), setSaving: setSavingPericialCard }
    );

  // ---- Inspección Ocular ----
  const startEditInspeccionCard = () => {
    setEditingInspeccionForm(
      inspeccionItemsActora.map((item) => ({
        _id: genRowId(),
        tipo: splitJoinedTipo(item?.tipo),
        nombre: item?.nombre || "",
        objeto: item?.objeto || "",
      }))
    );
    setEditingInspeccionCard(true);
  };

  const handleSaveInspeccionCard = () =>
    savePruebaSection(
      {
        pruebasActora: {
          inspeccion_ocular: editingInspeccionForm.map((item) => ({
            tipo_inspeccion_ocular: item.tipo || [],
            nombre_inspeccion_ocular: (item.nombre || "").trim(),
            objeto_prueba: (item.objeto || "").trim(),
          })),
        },
      },
      { onDone: () => setEditingInspeccionCard(false), setSaving: setSavingInspeccionCard }
    );

  // ---- Switches de estatus ----
  const handleToggleChecklist = async (key) => {
    setSavingChecklistKey(key);
    try {
      const payload = {
        pruebas_documentacion: buildPruebasDocumentacionPayload(
          {},
          { [key]: !checklistActora?.[key] }
        ),
      };
      await dispatch(actionJuntaUpdateById(idJunta, payload, {}));
      await load();
      notify.success({
        message: "Actualizado",
        description: "El estatus se guardó correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo actualizar",
        description: e?.response?.data?.detail || e?.message || "No se pudo guardar el cambio.",
        placement: "bottomRight",
      });
    } finally {
      setSavingChecklistKey(null);
    }
  };

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        <div>
          {/* SUMMARY CARD — mismas clases laboral-exp-* que usa el header de
              Tribunal, para que ícono, tamaños de texto y espaciados sean
              idénticos entre ambos módulos. */}
          <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
          <ConfigProvider wave={{ showEffect: showInsetEffect }}>
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          fontSize: 28,
                          fontWeight: 650,
                          letterSpacing: "-0.01em",
                          lineHeight: 1.2,
                          color: "#0f172a",
                        }}
                      >
                        {actorName.toUpperCase()}
                        {empresaName !== "—" ? ` VS ${empresaName.toUpperCase()}` : ""}
                      </Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                    <Text type="secondary" className="laboral-exp-subtitle">
                      Núm. de expediente: {detalle?.expediente || "Sin número"}
                    </Text>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-location-dot" />
                      <Text>Creado: {ubicacionName}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>Creado: {fmtDateLong(detalle?.datetime)}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-address-card" />
                      <Text>Creado por: {toTitleCase(detalle?.nombre_usuario)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  <Button
                    danger
                    type="primary"
                    icon={<i className="fa-solid fa-trash" />}
                    style={{ borderRadius: 8 }}
                    onClick={handleDelete}
                    loading={deleting}
                    disabled={deleting}
                  >
                    Eliminar
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* TABS BAR */}
            <div className="laboral-exp-header-tabs">
              {[
                { key: "1", label: "Principal",       icon: <i className="fa-solid fa-file" /> },
                { key: "2", label: "Proceso",         icon: <i className="fa-solid fa-diagram-project" /> },
                { key: "3", label: "Cuantificaciones",icon: <i className="fa-solid fa-calculator" /> },
                { key: "5", label: "Terminación",     icon: <i className="fa-solid fa-scale-balanced" /> },
                { key: "7", label: "Documentos",      icon: <i className="fa-solid fa-folder-open" /> },
                { key: "6", label: "Actividad",       icon: <i className="fa-solid fa-list-check" /> },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
          </ConfigProvider>
          </div>

          {/* TAB: PRINCIPAL */}
          {activeTab === "1" && (
            <>
              {/* Datos del expediente + cuadro azul de "Próxima audiencia"
                  (mismo patrón que el expediente de Centro de Conciliación). */}
              <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 18 }}>
              <Col xs={24} lg={20}>
              <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ height: "100%" }}>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fa-solid fa-file" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Datos del Expediente</h3>
                  </div>

                  {!editingDatosCard && (
                    <Button onClick={startEditDatosCard} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {editingDatosCard ? (
                  <div className="laboral-exp-edit-form">
                    <div className="laboral-exp-edit-grid">
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Estado</span>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Selecciona un estado"
                          options={estadosOptionsFinal}
                          value={selectedEstadoId}
                          onChange={(v) => {
                            setSelectedEstadoId(v ?? null);
                            setSelectedCiudadId(null);
                            setSelectedAutoridadId(null);
                          }}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Ciudad</span>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                          disabled={!selectedEstadoId}
                          placeholder={!selectedEstadoId ? "Selecciona un estado primero" : "Selecciona una ciudad"}
                          options={ciudadesOptionsFinal}
                          value={selectedCiudadId}
                          onChange={(v) => {
                            setSelectedCiudadId(v ?? null);
                            setSelectedAutoridadId(null);
                          }}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Autoridad</span>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                          disabled={!selectedCiudadId}
                          placeholder={!selectedCiudadId ? "Selecciona una ciudad primero" : "Selecciona una autoridad"}
                          options={autoridadOptions}
                          value={selectedAutoridadId}
                          onChange={(v) => setSelectedAutoridadId(v ?? null)}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Número de Expediente</span>
                        <Input
                          value={editingDatosForm.expediente}
                          onChange={(e) => setEditingDatosForm((f) => ({ ...f, expediente: e.target.value }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Nombre de la Parte Actora</span>
                        <Input
                          value={editingDatosForm.nombre_actora}
                          onChange={(e) => setEditingDatosForm((f) => ({ ...f, nombre_actora: e.target.value }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Razón Social</span>
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Seleccionar razón(es) social(es)"
                          options={todasRazonesOptions}
                          value={editingDatosForm.razones_sociales_ids || []}
                          onChange={(v) => setEditingDatosForm((f) => ({ ...f, razones_sociales_ids: v }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Abogado</span>
                        <Input
                          value={editingDatosForm.abogado}
                          onChange={(e) => setEditingDatosForm((f) => ({ ...f, abogado: e.target.value }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Corresponsal (nombre)</span>
                        <Input
                          value={editingDatosForm.corresponsal_nombre}
                          onChange={(e) => setEditingDatosForm((f) => ({ ...f, corresponsal_nombre: e.target.value }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Competencia</span>
                        <Select
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="Seleccionar competencia"
                          options={COMPETENCIA_OPTS}
                          value={editingDatosForm.competencia || undefined}
                          onChange={(v) => setEditingDatosForm((f) => ({ ...f, competencia: v || "" }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Tipo de Procedimiento</span>
                        <Select
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="Seleccionar procedimiento"
                          options={TIPO_PROCEDIMIENTO_OPTS}
                          value={editingDatosForm.tipo_procedimiento || undefined}
                          onChange={(v) => setEditingDatosForm((f) => ({ ...f, tipo_procedimiento: v || "" }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Expediente(s) Acumulado(s) o Relacionado(s)</span>
                        <Input
                          placeholder="Ej. 0457/2019, 0461/2019"
                          value={editingDatosForm.expedientes_relacionados}
                          onChange={(e) => setEditingDatosForm((f) => ({ ...f, expedientes_relacionados: e.target.value }))}
                        />
                      </div>

                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Régimen (Reforma Laboral 2012)</span>
                        <Select
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="Seleccionar régimen"
                          options={REGIMEN_REFORMA_2012_OPTS}
                          value={editingDatosForm.regimen_reforma_2012 || undefined}
                          onChange={(v) => setEditingDatosForm((f) => ({ ...f, regimen_reforma_2012: v || "" }))}
                        />
                      </div>
                    </div>

                    <div className="laboral-exp-edit-actions">
                      <Button onClick={() => setEditingDatosCard(false)} className="laboral-exp-btn-clean">
                        Cancelar
                      </Button>
                      <HappyProvider>
                        <Button
                          type="primary"
                          loading={savingDatosCard}
                          onClick={handleSaveDatosCard}
                          className="laboral-exp-btn-save"
                          icon={<i className="fa-solid fa-floppy-disk" />}
                        >
                          Guardar
                        </Button>
                      </HappyProvider>
                    </div>
                  </div>
                ) : (
                  <div className="laboral-exp-field-grid">
                    <Field label="Número de Expediente" value={detalle?.expediente || "—"} />
                    <Field label="Estado" value={isActive ? "Activo" : "Archivado"} />
                    <Field label="Parte Actora" value={actorName} />
                    <Field label="Demandada / Razón Social" value={empresaName} />
                    <Field label="Ciudad" value={ciudadName || "—"} />
                    <Field label="Estado (entidad)" value={estadoName || "—"} />
                    <Field label="Autoridad" value={autoridadName} />
                    <Field label="Competencia" value={detalle?.competencia || "—"} />
                    <Field label="Tipo de Procedimiento" value={detalle?.tipo_procedimiento || "—"} />
                    <Field label="Expediente(s) Acumulado(s) o Relacionado(s)" value={detalle?.expedientes_relacionados || "—"} />
                    <Field
                      label="Régimen (Reforma Laboral 2012)"
                      value={REGIMEN_REFORMA_2012_LABELS[detalle?.regimen_reforma_2012] || "—"}
                    />
                  </div>
                )}
              </Card>
              </Col>

              {/* CUADRO AZUL — próxima audiencia agendada (la más reciente
                  entre todas las etapas del proceso) */}
              <Col xs={24} lg={4}>
                <div className="laboral-exp-next-aud-card">
                  <div className="laboral-exp-next-aud-header">
                    <span className="laboral-exp-chip-label">
                      {proximaAudienciaJunta
                        ? `Próxima audiencia — Audiencia ${idJunta}`
                        : "Próxima audiencia"}
                    </span>
                  </div>
                  <div className="laboral-exp-next-aud-body">
                    <Text className="laboral-exp-next-aud-date">
                      {proximaAudienciaJunta
                        ? proximaAudienciaJunta.format("DD [de] MMMM [de] YYYY")
                        : "Pendiente de programar"}
                    </Text>
                    {proximaAudienciaJunta && (
                      <Text className="laboral-exp-next-aud-sub" style={{ display: "block", marginTop: 6 }}>
                        {proximaAudienciaJunta.format("HH:mm [hrs]")}
                      </Text>
                    )}
                  </div>
                </div>
              </Col>
              </Row>

              {/* Partes y Representación */}
              <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fa-solid fa-users" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Partes y Representación</h3>
                  </div>
                  {!editingPartesCard && (
                    <Button onClick={startEditPartesCard} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {editingPartesCard ? (
                  <div className="laboral-exp-edit-form">
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Parte(s) Actora(s)</span>
                      {editingPartesForm.actores.map((actor, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                          <Input
                            style={{ flex: 1 }}
                            value={actor}
                            placeholder={`Actor ${idx + 1}`}
                            onChange={(e) =>
                              setEditingPartesForm((f) => ({
                                ...f,
                                actores: f.actores.map((a, i) => (i === idx ? e.target.value : a)),
                              }))
                            }
                          />
                          <Button
                            className="laboral-exp-btn-clean"
                            disabled={editingPartesForm.actores.length <= 1}
                            onClick={() =>
                              setEditingPartesForm((f) => ({
                                ...f,
                                actores: f.actores.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            Quitar
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingPartesForm((f) => ({ ...f, actores: [...f.actores, ""] }))
                        }
                      >
                        Agregar otro actor
                      </Button>
                    </div>

                    <div className="laboral-exp-edit-field" style={{ marginTop: 16 }}>
                      <span className="laboral-exp-edit-label">Demandado(s)</span>
                      {editingPartesForm.demandados.map((d, idx) => (
                        <div key={d._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Demandado {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              disabled={editingPartesForm.demandados.length <= 1}
                              onClick={() =>
                                setEditingPartesForm((f) => ({
                                  ...f,
                                  demandados: f.demandados.filter((r) => r._id !== d._id),
                                }))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Razón social</span>
                              <Select
                                showSearch
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                placeholder="Selecciona la razón social"
                                options={todasRazonesOptions}
                                value={d.id_razon_social ?? undefined}
                                onChange={(v) =>
                                  setEditingPartesForm((f) => ({
                                    ...f,
                                    demandados: f.demandados.map((r) =>
                                      r._id === d._id ? { ...r, id_razon_social: v ?? null } : r
                                    ),
                                  }))
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Carácter con que se le demanda</span>
                              <Select
                                allowClear
                                style={{ width: "100%" }}
                                placeholder="Selecciona el carácter"
                                options={CARACTER_DEMANDADO_OPTS}
                                value={d.caracter || undefined}
                                onChange={(v) =>
                                  setEditingPartesForm((f) => ({
                                    ...f,
                                    demandados: f.demandados.map((r) =>
                                      r._id === d._id ? { ...r, caracter: v || "" } : r
                                    ),
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingPartesForm((f) => ({
                            ...f,
                            demandados: [...f.demandados, { _id: genRowId(), id_razon_social: null, caracter: "" }],
                          }))
                        }
                      >
                        Agregar otro demandado
                      </Button>
                    </div>

                    <div className="laboral-exp-edit-grid" style={{ marginTop: 16 }}>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Tipo de Asesoría de la Actora</span>
                        <Select
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Seleccionar"
                          options={TIPO_ASESORIA_OPTS}
                          value={editingPartesForm.tipo_asesoria_actora || undefined}
                          onChange={(v) =>
                            setEditingPartesForm((f) => ({ ...f, tipo_asesoria_actora: v || "" }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Abogado Responsable del Despacho</span>
                        <Select
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          placeholder="Seleccionar abogado"
                          options={abogadosOptions}
                          value={editingPartesForm.id_abogado_responsable || undefined}
                          onChange={(v) =>
                            setEditingPartesForm((f) => ({ ...f, id_abogado_responsable: v || null }))
                          }
                        />
                      </div>
                    </div>

                    <div className="laboral-exp-edit-actions">
                      <Button onClick={() => setEditingPartesCard(false)} className="laboral-exp-btn-clean">
                        Cancelar
                      </Button>
                      <HappyProvider>
                        <Button
                          type="primary"
                          loading={savingPartesCard}
                          onClick={handleSavePartesCard}
                          className="laboral-exp-btn-save"
                          icon={<i className="fa-solid fa-floppy-disk" />}
                        >
                          Guardar
                        </Button>
                      </HappyProvider>
                    </div>
                  </div>
                ) : (
                  <div className="laboral-exp-field-grid">
                    <Field
                      label="Parte(s) Actora(s)"
                      value={actoresList.length ? actoresList.join(", ") : "—"}
                    />
                    <Field
                      label="Demandado(s)"
                      value={
                        demandadosList.length
                          ? demandadosList
                              .map((d) => (d.caracter ? `${d.nombre} (${d.caracter})` : d.nombre))
                              .join(", ")
                          : "—"
                      }
                    />
                    <Field label="Tipo de Asesoría de la Actora" value={detalle?.tipo_asesoria_actora || "—"} />
                    <Field label="Abogado Responsable del Despacho" value={abogadoResponsableName} />
                  </div>
                )}
              </Card>

              {/* Estado del expediente al darlo de alta */}
              <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fa-solid fa-timeline" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Estado del Expediente al Darlo de Alta</h3>
                  </div>
                  {!editingEstadoProcesalCard && (
                    <Button onClick={startEditEstadoProcesalCard} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {editingEstadoProcesalCard ? (
                  <div className="laboral-exp-edit-form">
                    <div className="laboral-exp-edit-grid">
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Etapa Procesal Actual</span>
                        <Select
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Seleccionar etapa"
                          options={ETAPA_PROCESAL_OPTS}
                          value={editingEstadoProcesalForm.etapa_procesal_inicial || undefined}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, etapa_procesal_inicial: v || "" }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Acción Intentada</span>
                        <Select
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Seleccionar acción"
                          options={ACCION_INTENTADA_OPTS}
                          value={editingEstadoProcesalForm.accion_intentada || undefined}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, accion_intentada: v || "" }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Fecha de Presentación de la Demanda</span>
                        <ScrollDatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={editingEstadoProcesalForm.fecha_presentacion_demanda}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, fecha_presentacion_demanda: v }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Fecha de Recepción en el Despacho</span>
                        <ScrollDatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={editingEstadoProcesalForm.fecha_recepcion}
                          onChange={(v) => setEditingEstadoProcesalForm((f) => ({ ...f, fecha_recepcion: v }))}
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Fecha de Emplazamiento</span>
                        <ScrollDatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={editingEstadoProcesalForm.fecha_emplazamiento}
                          onChange={(v) => setEditingEstadoProcesalForm((f) => ({ ...f, fecha_emplazamiento: v }))}
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Fecha de la Última Promoción o Actuación</span>
                        <ScrollDatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={editingEstadoProcesalForm.fecha_ultima_actuacion}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, fecha_ultima_actuacion: v }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Próxima Diligencia Programada</span>
                        <ScrollDatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={editingEstadoProcesalForm.fecha_proxima_diligencia}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, fecha_proxima_diligencia: v }))
                          }
                        />
                      </div>
                      <div className="laboral-exp-edit-field">
                        <span className="laboral-exp-edit-label">Descripción de la Próxima Diligencia</span>
                        <Select
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Seleccionar diligencia"
                          options={DILIGENCIA_OPTS}
                          value={editingEstadoProcesalForm.descripcion_proxima_diligencia || undefined}
                          onChange={(v) =>
                            setEditingEstadoProcesalForm((f) => ({ ...f, descripcion_proxima_diligencia: v || "" }))
                          }
                        />
                      </div>
                    </div>

                    <div className="laboral-exp-edit-actions">
                      <Button onClick={() => setEditingEstadoProcesalCard(false)} className="laboral-exp-btn-clean">
                        Cancelar
                      </Button>
                      <HappyProvider>
                        <Button
                          type="primary"
                          loading={savingEstadoProcesalCard}
                          onClick={handleSaveEstadoProcesalCard}
                          className="laboral-exp-btn-save"
                          icon={<i className="fa-solid fa-floppy-disk" />}
                        >
                          Guardar
                        </Button>
                      </HappyProvider>
                    </div>
                  </div>
                ) : (
                  <div className="laboral-exp-field-grid">
                    <Field label="Etapa Procesal Actual" value={detalle?.etapa_procesal_inicial || "—"} />
                    <Field label="Acción Intentada" value={detalle?.accion_intentada || "—"} />
                    <Field label="Fecha de Presentación de la Demanda" value={fmtDateLong(detalle?.fecha_presentacion_demanda)} />
                    <Field label="Fecha de Recepción en el Despacho" value={fmtDateLong(detalle?.fecha_recepcion)} />
                    <Field label="Fecha de Emplazamiento" value={fmtDateLong(detalle?.fecha_emplazamiento)} />
                    <Field label="Fecha de la Última Promoción o Actuación" value={fmtDateLong(detalle?.fecha_ultima_actuacion)} />
                    <Field label="Próxima Diligencia Programada" value={fmtDateLong(detalle?.fecha_proxima_diligencia)} />
                    <Field label="Descripción de la Próxima Diligencia" value={detalle?.descripcion_proxima_diligencia || "—"} />
                  </div>
                )}
              </Card>

              {/* Comentarios */}
              {detalle?.comentarios && (
                <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
                  <div className="laboral-exp-card-header">
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-comment-dots" />
                      </div>
                      <h3 className="laboral-exp-card-header-title">Comentarios y Observaciones</h3>
                    </div>
                  </div>
                  <p style={{ color: "#475569", lineHeight: 1.7, margin: 0 }}>
                    {detalle.comentarios}
                  </p>
                </Card>
              )}

              {/* Pruebas del expediente — copiado tal cual del detalle de
                  Tribunal (LaboralTribunalDetallePage.jsx), mismas clases
                  lxp-* / laboral-exp-*, para que luzca idéntico. */}
              <div className="lxp-pruebas-wrap" id="lxp-pruebas-expediente-section">
                <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginBottom: 18 }}>
                  <div className="laboral-exp-card-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-folder-open" />
                      </div>
                      <div>
                        <h3 className="laboral-exp-card-header-title">Pruebas del Expediente</h3>
                        <div className="lxp-pruebas-subtitle">
                          Resumen de pruebas y evidencias registradas en el proceso judicial.
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {confesionalesActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-magnifying-glass" />
                      </div>
                      <h3 className="lxp-section-title">
                        Confesional Demandada{" "}
                        <span className="lxp-section-count">({confesionalesActora.length})</span>
                      </h3>
                    </div>
                    {!editingConfesionalCard && (
                      <Button onClick={startEditConfesionalCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingConfesionalCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingConfesionalForm.map((item, idx) => (
                        <div key={item._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Confesional {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingConfesionalForm((rows) => rows.filter((r) => r._id !== item._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Persona a confesar</span>
                              <Input
                                value={item.persona}
                                onChange={(e) =>
                                  setEditingConfesionalForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, persona: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Hechos a confesar</span>
                              <Input.TextArea
                                rows={2}
                                value={item.hechos}
                                onChange={(e) =>
                                  setEditingConfesionalForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, hechos: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingConfesionalForm((rows) => [
                            ...rows,
                            { _id: genRowId(), persona: "", hechos: "" },
                          ])
                        }
                      >
                        Agregar confesional
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingConfesionalCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingConfesionalCard}
                            onClick={handleSaveConfesionalCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : confesionalesActora.length > 0 ? (
                    <div className="lxp-prueba-stack">
                      {confesionalesActora.map((item, idx) => (
                        <div key={item?.id || `${item?.persona_confesar}-${idx}`} className="lxp-prueba-block">
                          <div className="lxp-prueba-grid lxp-prueba-grid--2">
                            <div className="lxp-kv">
                              <div className="lxp-k">Persona a confesar</div>
                              <div className="lxp-v">{item?.persona_confesar || "—"}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Hechos a confesar</div>
                              <div className="lxp-v lxp-prueba-text">{item?.hechos_confesar || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin confesional registrada</div>
                  )}
                </section>
                )}

                {confesionalesPropiosActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-user-check" />
                      </div>
                      <h3 className="lxp-section-title">
                        Confesional Hechos Propios{" "}
                        <span className="lxp-section-count">({confesionalesPropiosActora.length})</span>
                      </h3>
                    </div>
                    {!editingConfesionalPropiosCard && (
                      <Button onClick={startEditConfesionalPropiosCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingConfesionalPropiosCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingConfesionalPropiosForm.map((item, idx) => (
                        <div key={item._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Confesional de hechos propios {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingConfesionalPropiosForm((rows) => rows.filter((r) => r._id !== item._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Tipo de prueba</span>
                              <Input
                                value={item.tipo_prueba}
                                onChange={(e) =>
                                  setEditingConfesionalPropiosForm((rows) =>
                                    rows.map((r) =>
                                      r._id === item._id ? { ...r, tipo_prueba: e.target.value } : r
                                    )
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Hechos propios</span>
                              <Input.TextArea
                                rows={2}
                                value={item.hechos_propios}
                                onChange={(e) =>
                                  setEditingConfesionalPropiosForm((rows) =>
                                    rows.map((r) =>
                                      r._id === item._id ? { ...r, hechos_propios: e.target.value } : r
                                    )
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingConfesionalPropiosForm((rows) => [
                            ...rows,
                            { _id: genRowId(), tipo_prueba: "", hechos_propios: "" },
                          ])
                        }
                      >
                        Agregar confesional de hechos propios
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button
                          onClick={() => setEditingConfesionalPropiosCard(false)}
                          className="laboral-exp-btn-clean"
                        >
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingConfesionalPropiosCard}
                            onClick={handleSaveConfesionalPropiosCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : confesionalesPropiosActora.length > 0 ? (
                    <div className="lxp-prueba-stack">
                      {confesionalesPropiosActora.map((item, idx) => (
                        <div key={item?.id || `${item?.tipo_prueba}-${idx}`} className="lxp-prueba-block">
                          <div className="lxp-prueba-grid lxp-prueba-grid--2">
                            <div className="lxp-kv">
                              <div className="lxp-k">Tipo de prueba</div>
                              <div className="lxp-v">{item?.tipo_prueba || "—"}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Hechos propios</div>
                              <div className="lxp-v lxp-prueba-text">{item?.hechos_propios || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin confesional de hechos propios registrada</div>
                  )}
                </section>
                )}

                {confesionalesDemandada.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-person-circle-question" />
                      </div>
                      <h3 className="lxp-section-title">
                        Confesional Demandada (Empresa Vinculada){" "}
                        <span className="lxp-section-count">({confesionalesDemandada.length})</span>
                      </h3>
                    </div>
                    {!editingConfesionalDemandadaCard && (
                      <Button onClick={startEditConfesionalDemandadaCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingConfesionalDemandadaCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingConfesionalDemandadaForm.map((item, idx) => (
                        <div key={item._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Confesional demandada {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingConfesionalDemandadaForm((rows) =>
                                  rows.filter((r) => r._id !== item._id)
                                )
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-field">
                            <span className="laboral-exp-edit-label">Razón social demandada</span>
                            <Select
                              showSearch
                              optionFilterProp="label"
                              style={{ width: "100%" }}
                              placeholder="Selecciona la razón social"
                              options={razonesSocialesDemandadaOptions}
                              value={item.id_razon_social ?? undefined}
                              onChange={(v) =>
                                setEditingConfesionalDemandadaForm((rows) =>
                                  rows.map((r) => (r._id === item._id ? { ...r, id_razon_social: v ?? null } : r))
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingConfesionalDemandadaForm((rows) => [
                            ...rows,
                            { _id: genRowId(), id_razon_social: null },
                          ])
                        }
                      >
                        Agregar confesional demandada
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button
                          onClick={() => setEditingConfesionalDemandadaCard(false)}
                          className="laboral-exp-btn-clean"
                        >
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingConfesionalDemandadaCard}
                            onClick={handleSaveConfesionalDemandadaCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : confesionalesDemandada.length > 0 ? (
                    <div className="lxp-prueba-stack">
                      {confesionalesDemandada.map((item, idx) => (
                        <div key={item?.id || `${item?.empresa_nombre}-${idx}`} className="lxp-prueba-block">
                          <div className="lxp-prueba-block-title">
                            Confesional Demandada — {item?.razon_social || item?.empresa_nombre || "—"}
                          </div>
                          <div className="lxp-prueba-grid lxp-prueba-grid--2">
                            <div className="lxp-kv">
                              <div className="lxp-k">Empresa</div>
                              <div className="lxp-v">{item?.empresa_nombre || "—"}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Razón social</div>
                              <div className="lxp-v">{item?.razon_social || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin confesional demandada</div>
                  )}
                </section>
                )}

                {informesAutoridadActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-landmark" />
                      </div>
                      <h3 className="lxp-section-title">Informe de Autoridad</h3>
                    </div>
                    {!editingInformeAutoridadCard && (
                      <Button onClick={startEditInformeAutoridadCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingInformeAutoridadCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingInformeAutoridadForm.map((doc, idx) => (
                        <div key={doc._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Documento {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingInformeAutoridadForm((rows) => rows.filter((r) => r._id !== doc._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Nombre</span>
                              <Input
                                value={doc.nombre}
                                onChange={(e) =>
                                  setEditingInformeAutoridadForm((rows) =>
                                    rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Fecha</span>
                              <ScrollDatePicker
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                value={doc.fecha}
                                onChange={(v) =>
                                  setEditingInformeAutoridadForm((rows) =>
                                    rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="laboral-exp-edit-label">Descripción</span>
                              <Input.TextArea
                                rows={2}
                                value={doc.descripcion}
                                onChange={(e) =>
                                  setEditingInformeAutoridadForm((rows) =>
                                    rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingInformeAutoridadForm((rows) => [
                            ...rows,
                            { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                          ])
                        }
                      >
                        Agregar documento
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button
                          onClick={() => setEditingInformeAutoridadCard(false)}
                          className="laboral-exp-btn-clean"
                        >
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingInformeAutoridadCard}
                            onClick={handleSaveInformeAutoridadCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : (
                    <div className="lxp-doc-list">
                      {informesAutoridadActora.length > 0 ? (
                        informesAutoridadActora.map((doc, idx) => (
                          <div key={doc?.id || `${doc?.nombre}-${idx}`} className="lxp-doc-item">
                            <span className="material-symbols-outlined lxp-doc-icon">description</span>
                            <div>
                              <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                              <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                              <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="lxp-empty-inline">Sin informes de autoridad</div>
                      )}
                    </div>
                  )}
                </section>
                )}

                {testigosActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-users" />
                      </div>
                      <h3 className="lxp-section-title">Testimonial</h3>
                    </div>
                    {!editingTestimonialCard && (
                      <Button onClick={startEditTestimonialCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingTestimonialCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingTestimonialForm.map((t, idx) => (
                        <div key={t._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Testigo {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingTestimonialForm((rows) => rows.filter((r) => r._id !== t._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Nombre</span>
                              <Input
                                value={t.nombre}
                                onChange={(e) =>
                                  setEditingTestimonialForm((rows) =>
                                    rows.map((r) => (r._id === t._id ? { ...r, nombre: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Relación</span>
                              <Input
                                value={t.relacion}
                                onChange={(e) =>
                                  setEditingTestimonialForm((rows) =>
                                    rows.map((r) => (r._id === t._id ? { ...r, relacion: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Contacto</span>
                              <Input
                                value={t.contacto}
                                onChange={(e) =>
                                  setEditingTestimonialForm((rows) =>
                                    rows.map((r) => (r._id === t._id ? { ...r, contacto: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingTestimonialForm((rows) => [
                            ...rows,
                            { _id: genRowId(), nombre: "", relacion: "", contacto: "" },
                          ])
                        }
                      >
                        Agregar testigo
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingTestimonialCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingTestimonialCard}
                            onClick={handleSaveTestimonialCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : testigosActora.length > 0 ? (
                    <div className="lxp-testigos-grid">
                      {testigosActora.map((testigo, idx) => (
                        <div key={testigo?.id || `${testigo?.nombre}-${idx}`} className="lxp-testigo-card">
                          <div className="lxp-testigo-avatar">
                            <span className="material-symbols-outlined">person</span>
                          </div>

                          <div className="lxp-testigo-body">
                            <div className="lxp-testigo-name">{testigo?.nombre || "—"}</div>

                            <div className="lxp-testigo-meta">
                              <div className="lxp-testigo-kv">
                                <div className="lxp-testigo-k">Relación</div>
                                <div className="lxp-testigo-v">{testigo?.relacion || "—"}</div>
                              </div>

                              <div className="lxp-testigo-kv">
                                <div className="lxp-testigo-k">Contacto</div>
                                <div className="lxp-testigo-v">{testigo?.contacto || "—"}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin testigos registrados</div>
                  )}
                </section>
                )}

                {(documentosPublicosActora.length > 0 || documentosPrivadosActora.length > 0) && (
                <div className="lxp-docs-grid">
                  {documentosPublicosActora.length > 0 && (
                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-file-circle-check" />
                        </div>
                        <h3 className="lxp-section-title">Documental Pública</h3>
                      </div>
                      {!editingDocPublicaCard && (
                        <Button onClick={startEditDocPublicaCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingDocPublicaCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingDocPublicaForm.map((doc, idx) => (
                          <div key={doc._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Documento {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingDocPublicaForm((rows) => rows.filter((r) => r._id !== doc._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Nombre</span>
                                <Input
                                  value={doc.nombre}
                                  onChange={(e) =>
                                    setEditingDocPublicaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Fecha</span>
                                <ScrollDatePicker
                                  style={{ width: "100%" }}
                                  format="DD/MM/YYYY"
                                  value={doc.fecha}
                                  onChange={(v) =>
                                    setEditingDocPublicaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                <span className="laboral-exp-edit-label">Descripción</span>
                                <Input.TextArea
                                  rows={2}
                                  value={doc.descripcion}
                                  onChange={(e) =>
                                    setEditingDocPublicaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingDocPublicaForm((rows) => [
                              ...rows,
                              { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                            ])
                          }
                        >
                          Agregar documento
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingDocPublicaCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingDocPublicaCard}
                              onClick={handleSaveDocPublicaCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : (
                      <div className="lxp-doc-list">
                        {documentosPublicosActora.length ? (
                          documentosPublicosActora.map((doc) => (
                            <div key={doc?.id || doc?.nombre} className="lxp-doc-item">
                              <span className="material-symbols-outlined lxp-doc-icon">description</span>
                              <div>
                                <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                                <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                                <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="lxp-empty-inline">Sin documentos públicos</div>
                        )}
                      </div>
                    )}
                  </section>
                  )}

                  {documentosPrivadosActora.length > 0 && (
                  <section className="lxp-prueba-card">
                    <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-file-lines" />
                        </div>
                        <h3 className="lxp-section-title">Documental Privada</h3>
                      </div>
                      {!editingDocPrivadaCard && (
                        <Button onClick={startEditDocPrivadaCard} className="laboral-exp-btn-clean">
                          Editar
                        </Button>
                      )}
                    </div>

                    {editingDocPrivadaCard ? (
                      <div className="laboral-exp-edit-form">
                        {editingDocPrivadaForm.map((doc, idx) => (
                          <div key={doc._id} className="lxp-edit-row-card">
                            <div className="lxp-edit-row-head">
                              <span>Documento {idx + 1}</span>
                              <Button
                                size="small"
                                className="laboral-exp-btn-clean"
                                onClick={() =>
                                  setEditingDocPrivadaForm((rows) => rows.filter((r) => r._id !== doc._id))
                                }
                              >
                                Quitar
                              </Button>
                            </div>
                            <div className="laboral-exp-edit-grid">
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Nombre</span>
                                <Input
                                  value={doc.nombre}
                                  onChange={(e) =>
                                    setEditingDocPrivadaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, nombre: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field">
                                <span className="laboral-exp-edit-label">Fecha</span>
                                <ScrollDatePicker
                                  style={{ width: "100%" }}
                                  format="DD/MM/YYYY"
                                  value={doc.fecha}
                                  onChange={(v) =>
                                    setEditingDocPrivadaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, fecha: v } : r))
                                    )
                                  }
                                />
                              </div>
                              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                                <span className="laboral-exp-edit-label">Descripción</span>
                                <Input.TextArea
                                  rows={2}
                                  value={doc.descripcion}
                                  onChange={(e) =>
                                    setEditingDocPrivadaForm((rows) =>
                                      rows.map((r) => (r._id === doc._id ? { ...r, descripcion: e.target.value } : r))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          block
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() =>
                            setEditingDocPrivadaForm((rows) => [
                              ...rows,
                              { _id: genRowId(), nombre: "", descripcion: "", fecha: null },
                            ])
                          }
                        >
                          Agregar documento
                        </Button>

                        <div className="laboral-exp-edit-actions">
                          <Button onClick={() => setEditingDocPrivadaCard(false)} className="laboral-exp-btn-clean">
                            Cancelar
                          </Button>
                          <HappyProvider>
                            <Button
                              type="primary"
                              loading={savingDocPrivadaCard}
                              onClick={handleSaveDocPrivadaCard}
                              className="laboral-exp-btn-save"
                              icon={<i className="fa-solid fa-floppy-disk" />}
                            >
                              Guardar
                            </Button>
                          </HappyProvider>
                        </div>
                      </div>
                    ) : (
                      <div className="lxp-doc-list">
                        {documentosPrivadosActora.length ? (
                          documentosPrivadosActora.map((doc) => (
                            <div key={doc?.id || doc?.nombre} className="lxp-doc-item">
                              <span className="material-symbols-outlined lxp-doc-icon">description</span>
                              <div>
                                <div className="lxp-doc-name">{doc?.nombre || "—"}</div>
                                <div className="lxp-doc-desc">{doc?.descripcion || "—"}</div>
                                <div className="lxp-doc-date">{fmtShort(doc?.fecha)}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="lxp-empty-inline">Sin documentos privados</div>
                        )}
                      </div>
                    )}
                  </section>
                  )}
                </div>
                )}

                {pericialItemsActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-magnifying-glass-chart" />
                      </div>
                      <h3 className="lxp-section-title">Pericial</h3>
                    </div>
                    {!editingPericialCard && (
                      <Button onClick={startEditPericialCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingPericialCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingPericialForm.map((item, idx) => (
                        <div key={item._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Pericial {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingPericialForm((rows) => rows.filter((r) => r._id !== item._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="laboral-exp-edit-label">Tipo de pericial</span>
                              <Select
                                mode="multiple"
                                showSearch
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                placeholder="Selecciona uno o varios tipos"
                                options={PERICIAL_TIPO_OPTIONS}
                                value={item.tipo}
                                onChange={(v) =>
                                  setEditingPericialForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, tipo: v } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Perito propuesto</span>
                              <Input
                                value={item.perito}
                                onChange={(e) =>
                                  setEditingPericialForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, perito: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Objeto de la prueba</span>
                              <Input.TextArea
                                rows={2}
                                value={item.objeto}
                                onChange={(e) =>
                                  setEditingPericialForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, objeto: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingPericialForm((rows) => [
                            ...rows,
                            { _id: genRowId(), tipo: [], perito: "", objeto: "" },
                          ])
                        }
                      >
                        Agregar pericial
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingPericialCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingPericialCard}
                            onClick={handleSavePericialCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : pericialItemsActora.length > 0 ? (
                    <div className="lxp-prueba-stack">
                      {pericialItemsActora.map((item, idx) => (
                        <div key={item?.id || idx} className="lxp-prueba-block">
                          <div className="lxp-prueba-grid lxp-prueba-grid--3">
                            <div className="lxp-kv">
                              <div className="lxp-k">Tipo de pericial</div>
                              <div className="lxp-v">{formatTipoList(splitJoinedTipo(item?.tipo))}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Perito propuesto</div>
                              <div className="lxp-v">{item?.perito || "—"}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Objeto de la prueba</div>
                              <div className="lxp-v">{item?.objeto || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin pericial registrada</div>
                  )}
                </section>
                )}

                {inspeccionItemsActora.length > 0 && (
                <section className="lxp-prueba-card">
                  <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-eye" />
                      </div>
                      <h3 className="lxp-section-title">Inspección Ocular</h3>
                    </div>
                    {!editingInspeccionCard && (
                      <Button onClick={startEditInspeccionCard} className="laboral-exp-btn-clean">
                        Editar
                      </Button>
                    )}
                  </div>

                  {editingInspeccionCard ? (
                    <div className="laboral-exp-edit-form">
                      {editingInspeccionForm.map((item, idx) => (
                        <div key={item._id} className="lxp-edit-row-card">
                          <div className="lxp-edit-row-head">
                            <span>Inspección {idx + 1}</span>
                            <Button
                              size="small"
                              className="laboral-exp-btn-clean"
                              onClick={() =>
                                setEditingInspeccionForm((rows) => rows.filter((r) => r._id !== item._id))
                              }
                            >
                              Quitar
                            </Button>
                          </div>
                          <div className="laboral-exp-edit-grid">
                            <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                              <span className="laboral-exp-edit-label">Tipo de inspección ocular</span>
                              <Select
                                mode="multiple"
                                showSearch
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                placeholder="Selecciona uno o varios tipos"
                                options={INSPECCION_OCULAR_TIPO_OPTIONS}
                                value={item.tipo}
                                onChange={(v) =>
                                  setEditingInspeccionForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, tipo: v } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Nombre</span>
                              <Input
                                value={item.nombre}
                                onChange={(e) =>
                                  setEditingInspeccionForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, nombre: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                            <div className="laboral-exp-edit-field">
                              <span className="laboral-exp-edit-label">Objeto de la prueba</span>
                              <Input.TextArea
                                rows={2}
                                value={item.objeto}
                                onChange={(e) =>
                                  setEditingInspeccionForm((rows) =>
                                    rows.map((r) => (r._id === item._id ? { ...r, objeto: e.target.value } : r))
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        block
                        icon={<i className="fa-solid fa-plus" />}
                        onClick={() =>
                          setEditingInspeccionForm((rows) => [
                            ...rows,
                            { _id: genRowId(), tipo: [], nombre: "", objeto: "" },
                          ])
                        }
                      >
                        Agregar inspección
                      </Button>

                      <div className="laboral-exp-edit-actions">
                        <Button onClick={() => setEditingInspeccionCard(false)} className="laboral-exp-btn-clean">
                          Cancelar
                        </Button>
                        <HappyProvider>
                          <Button
                            type="primary"
                            loading={savingInspeccionCard}
                            onClick={handleSaveInspeccionCard}
                            className="laboral-exp-btn-save"
                            icon={<i className="fa-solid fa-floppy-disk" />}
                          >
                            Guardar
                          </Button>
                        </HappyProvider>
                      </div>
                    </div>
                  ) : inspeccionItemsActora.length > 0 ? (
                    <div className="lxp-prueba-stack">
                      {inspeccionItemsActora.map((item, idx) => (
                        <div key={item?.id || idx} className="lxp-prueba-block">
                          <div className="lxp-prueba-grid lxp-prueba-grid--2">
                            <div className="lxp-kv">
                              <div className="lxp-k">Tipo</div>
                              <div className="lxp-v">{formatTipoList(splitJoinedTipo(item?.tipo))}</div>
                            </div>
                            <div className="lxp-kv">
                              <div className="lxp-k">Nombre</div>
                              <div className="lxp-v">{item?.nombre || "—"}</div>
                            </div>
                          </div>
                          <div className="lxp-kv" style={{ marginTop: 12 }}>
                            <div className="lxp-k">Objeto</div>
                            <div className="lxp-v">{item?.objeto || "—"}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lxp-empty-inline">Sin inspección ocular registrada</div>
                  )}
                </section>
                )}

                <div className="lxp-status-grid">
                  <div className="lxp-status-card">
                    <div>
                      <div className="lxp-status-title">Instrumental de Actuaciones</div>
                      <div className="lxp-status-subtitle">Integrada formalmente al expediente</div>
                    </div>

                    <button
                      type="button"
                      className={`lxp-status-badge ${tieneInstrumentalActora ? "is-on" : "is-off"}`}
                      style={{ cursor: "pointer", border: "none" }}
                      disabled={savingChecklistKey === "instrumental_actuaciones"}
                      onClick={() => handleToggleChecklist("instrumental_actuaciones")}
                    >
                      <span className="material-symbols-outlined">
                        {tieneInstrumentalActora ? "check_circle" : "remove_circle"}
                      </span>
                      {tieneInstrumentalActora ? "Incluida" : "No incluida"}
                    </button>
                  </div>

                  <div className="lxp-status-card">
                    <div>
                      <div className="lxp-status-title">Presuncional Legal y Humana</div>
                      <div className="lxp-status-subtitle">Deducciones extraídas de hechos</div>
                    </div>

                    <button
                      type="button"
                      className={`lxp-status-badge ${tienePresuncionalActora ? "is-on" : "is-off"}`}
                      style={{ cursor: "pointer", border: "none" }}
                      disabled={savingChecklistKey === "presuncional_legal_humana"}
                      onClick={() => handleToggleChecklist("presuncional_legal_humana")}
                    >
                      <span className="material-symbols-outlined">
                        {tienePresuncionalActora ? "check_circle" : "remove_circle"}
                      </span>
                      {tienePresuncionalActora ? "Incluida" : "No incluida"}
                    </button>
                  </div>
                  <div className="lxp-status-card">
                    <div>
                      <div className="lxp-status-title">Confesional Expresa</div>
                      <div className="lxp-status-subtitle">Reconocimiento directo de hechos</div>
                    </div>

                    <button
                      type="button"
                      className={`lxp-status-badge ${tieneConfesionalExpresa ? "is-on" : "is-off"}`}
                      style={{ cursor: "pointer", border: "none" }}
                      disabled={savingChecklistKey === "confesional_expresa"}
                      onClick={() => handleToggleChecklist("confesional_expresa")}
                    >
                      <span className="material-symbols-outlined">
                        {tieneConfesionalExpresa ? "check_circle" : "remove_circle"}
                      </span>
                      {tieneConfesionalExpresa ? "Incluida" : "No incluida"}
                    </button>
                  </div>
                </div>
              </div>

              <section className="lxp-prueba-card" style={{ marginTop: 18 }}>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                      <i className="fa-solid fa-shield-halved" />
                    </div>
                    <h3 className="lxp-section-title">
                      Defensa{" "}
                      <span className="lxp-section-count">({defensa.documentos.length} doc.)</span>
                    </h3>
                  </div>
                  {!editingDefensaCard && (
                    <Button onClick={startEditDefensaCard} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {editingDefensaCard ? (
                  <div className="laboral-exp-edit-form">
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Notas de la defensa</span>
                      <Input.TextArea
                        rows={5}
                        value={editingDefensaTexto}
                        onChange={(e) => setEditingDefensaTexto(e.target.value)}
                        placeholder="Describe la estrategia / notas de defensa del expediente..."
                      />
                    </div>
                    <div className="laboral-exp-edit-actions">
                      <Button onClick={() => setEditingDefensaCard(false)} className="laboral-exp-btn-clean">
                        Cancelar
                      </Button>
                      <HappyProvider>
                        <Button
                          type="primary"
                          loading={savingDefensaCard}
                          onClick={handleSaveDefensaCard}
                          className="laboral-exp-btn-save"
                          icon={<i className="fa-solid fa-floppy-disk" />}
                        >
                          Guardar
                        </Button>
                      </HappyProvider>
                    </div>
                  </div>
                ) : (
                  <div className="lxp-prueba-stack">
                    <div className="lxp-prueba-block">
                      <div className="lxp-v lxp-prueba-text">
                        {defensa.texto || "Sin notas de defensa registradas"}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div className="laboral-exp-edit-label" style={{ marginBottom: 8 }}>
                    Documentos de la defensa
                  </div>

                  <Upload.Dragger
                    multiple
                    showUploadList={false}
                    disabled={uploadingDefensaDoc}
                    beforeUpload={handleUploadDefensaDoc}
                    style={{ marginBottom: 14 }}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {uploadingDefensaDoc ? "Subiendo documento..." : "Arrastra archivos aquí o haz clic para seleccionar"}
                    </p>
                    <p className="ant-upload-hint">Se subirán como documentos de defensa de este expediente.</p>
                  </Upload.Dragger>

                  {defensa.documentos.length === 0 ? (
                    <div className="lxp-empty-inline">Sin documentos de defensa</div>
                  ) : (
                    defensa.documentos.map((d) => (
                      <div
                        key={d.id_junta_defensa_documento || d.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: "1px solid #E2E8F0",
                          marginBottom: 8,
                          background: "#F8FAFC",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                          <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                            <i className="fa-solid fa-file-lines" />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {d.filename || d.path}
                            </div>
                            <div style={{ fontSize: 11, color: "#6B7280" }}>{fmtShort(d.datetime)}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                          {d?.url && (
                            <Button
                              size="small"
                              onClick={() => window.open(`${PATH}${d.url}`, "_blank")}
                              className="laboral-exp-btn-clean"
                            >
                              Ver
                            </Button>
                          )}
                          <Button size="small" danger onClick={() => handleDeleteDefensaDoc(d)}>
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

            </>
          )}

          {/* TAB: PROCESO */}
          {activeTab === "2" && (
            <EtapaProcesalJunta
              idJunta={idJunta}
              actorName={actorName}
              empresaName={empresaName}
              expediente={detalle?.expediente || ""}
              actoresList={actoresList}
              demandadosList={demandadosList}
              todasRazonesOptions={todasRazonesOptions}
              handleSavePartesJuicio={handleSavePartesJuicio}
              onFechaAudienciaChanged={loadProximaAudienciaJunta}
              regimenReforma2012={detalle?.regimen_reforma_2012 || ""}
              datosExpediente={{
                numero: detalle?.expediente || "—",
                estado: isActive ? "Activo" : "Archivado",
                parteActora: actorName,
                demandada: empresaName,
                ciudad: ciudadName || "—",
                estadoEntidad: estadoName || "—",
                autoridad: autoridadName,
                competencia: detalle?.competencia || "—",
                tipoProcedimiento: detalle?.tipo_procedimiento || "—",
                relacionados: detalle?.expedientes_relacionados || "—",
                regimenReforma2012: REGIMEN_REFORMA_2012_LABELS[detalle?.regimen_reforma_2012] || "—",
                partesActoras: actoresList.length ? actoresList.join(", ") : "—",
                demandados: demandadosList.length
                  ? demandadosList.map((d) => (d.caracter ? `${d.nombre} (${d.caracter})` : d.nombre)).join(", ")
                  : "—",
                tipoAsesoriaActora: detalle?.tipo_asesoria_actora || "—",
                abogado: detalle?.abogado || "—",
                abogadoResponsable: abogadoResponsableName,
                etapaProcesalInicial: detalle?.etapa_procesal_inicial || "—",
                accionIntentada: detalle?.accion_intentada || "—",
                fechaPresentacionDemanda: fmtDateLong(detalle?.fecha_presentacion_demanda),
                fechaRecepcion: fmtDateLong(detalle?.fecha_recepcion),
                fechaEmplazamiento: fmtDateLong(detalle?.fecha_emplazamiento),
                fechaUltimaActuacion: fmtDateLong(detalle?.fecha_ultima_actuacion),
                fechaProximaDiligencia: fmtDateLong(detalle?.fecha_proxima_diligencia),
                descripcionProximaDiligencia: detalle?.descripcion_proxima_diligencia || "—",
              }}
            />
          )}

          {/* TAB: CUANTIFICACIONES */}
          {activeTab === "3" && (
            <div style={{ marginTop: 18 }}>
              <TabCuantificaciones
                detalle={detalle}
                expedienteId={idJunta}
              />
            </div>
          )}

          {/* TAB: TERMINACIÓN */}
          {activeTab === "5" && (
            <div style={{ marginTop: 18 }}>
              <TabTerminacionJunta idJunta={idJunta} />
            </div>
          )}

          {/* TAB: DOCUMENTOS */}
          {activeTab === "7" && (
            <div style={{ marginTop: 18 }}>
              <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2">
                <div className="laboral-exp-card-header">
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fa-solid fa-folder-open" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Documentos de la Junta</h3>
                  </div>
                </div>

                <Upload.Dragger
                  multiple
                  showUploadList={false}
                  disabled={uploadingDoc}
                  customRequest={handleUploadDoc}
                  style={{ marginBottom: 20 }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {uploadingDoc ? "Subiendo documento..." : "Arrastra archivos aquí o haz clic para seleccionar"}
                  </p>
                  <p className="ant-upload-hint">Se subirán como documentos generales de este expediente.</p>
                </Upload.Dragger>

                {documentos.length === 0 ? (
                  <div className="lxp-empty-inline">Sin documentos subidos</div>
                ) : (
                  documentos.map((d) => (
                    <div
                      key={d.id_junta_documento}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid #E2E8F0",
                        marginBottom: 8,
                        background: "#F8FAFC",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                          <i className="fa-solid fa-file-lines" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.filename || d.path}
                          </div>
                          <div style={{ fontSize: 11, color: "#6B7280" }}>{fmtShort(d.datetime)}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                        <Button size="small" onClick={() => openJuntaDoc(d)} className="laboral-exp-btn-clean">
                          Ver
                        </Button>
                        <Button
                          size="small"
                          danger
                          loading={deletingDocId === d.id_junta_documento}
                          onClick={() => handleDeleteDoc(d.id_junta_documento)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </div>
          )}

          {/* TAB: ACTIVIDAD */}
          {activeTab === "6" && (
            <div style={{ marginTop: 18 }}>
              <MovimientosCard items={auditItems} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
